import { getCandidateProfileUrl } from '@/components/automation/MoveWithAutomationModal';
import type { EnqueueAutomationMoveInput } from './useEnqueueAutomationMove';
import type {
  WorkflowExecutionNextAction,
  WorkflowExecutionSummary,
} from './workflowExecutions.api';
import { firstStepAlreadyRan } from './firstStepAlreadyRan';
import type { UnderwayActivityDetails } from './types';

// Map the execution's `next_action` enrichment onto the copy details the row
// renders (interview mode, survey audience, reviewer/HM recipients). Same for
// every candidate in a bulk session — they share the rule/action.
const detailsFromNextAction = (
  nextAction: WorkflowExecutionNextAction | undefined
): UnderwayActivityDetails => ({
  scheduleMode: nextAction?.automation_mode,
  surveyType: nextAction?.survey_type,
  recipientKind: nextAction?.recipient_kind,
  recipients: nextAction?.recipients?.map((r) => ({
    name: r.name ?? '',
    avatarUrl: r.avatar_url ?? undefined,
  })),
});

// The FE undo popup only owns automation whose FIRST step runs immediately (it
// shows the 15s undo, then calls `/trigger`). A first step configured "with a
// delay" is scheduled and fired by the BE on its own clock, so it must NOT be
// enqueued/triggered here. An execution is delayed when the BE parked it for a
// future run (`scheduled_at`) OR its next (first) action carries a delay —
// checking both guards against either signal being unset.
export const isImmediateExecution = (
  execution: WorkflowExecutionSummary
): boolean => {
  const scheduledForFuture =
    execution.scheduled_at !== null &&
    execution.scheduled_at !== undefined &&
    Date.parse(execution.scheduled_at) > Date.now();
  const firstStepDelayed = (execution.next_action?.delay_seconds ?? 0) > 0;
  return !scheduledForFuture && !firstStepDelayed;
};

// Enqueue input for a set of pending executions; null when there's nothing to
// show — empty, or every execution is delayed (BE-scheduled). `isBulk` false
// renders the single-candidate row (name + avatar) instead of the collapsed
// "N candidates" strip.
export const buildEnqueueInputFromExecutions = (
  executions: WorkflowExecutionSummary[],
  options: { isBulk?: boolean } = {}
): EnqueueAutomationMoveInput | null => {
  // Drop delayed first-step moves (BE triggers those later itself) and re-entry
  // moves whose immediate first step already ran (BE would `already_created`-skip
  // them — they must not re-show in the popup).
  const immediate = executions.filter(
    (e) => isImmediateExecution(e) && !firstStepAlreadyRan(e.next_action)
  );
  if (immediate.length === 0) return null;

  const first = immediate[0];
  const fallbackTarget = first.workflow_rule?.actions?.[0]?.target;
  const activityKind =
    first.next_action?.kind ?? fallbackTarget?.kind ?? 'email';
  const activityLabel =
    first.next_action?.label ?? fallbackTarget?.label ?? activityKind;

  const ruleSummary = first.workflow_rule?.summary;

  return {
    activityKind,
    activityLabel,
    stageId: ruleSummary?.ats_job_stage?.id ?? '',
    stageName: ruleSummary?.ats_job_stage?.name ?? '',
    jobId: ruleSummary?.ats_job?.id ?? '',
    jobName: ruleSummary?.ats_job?.name ?? '',
    candidates: immediate.map((e) => ({
      id: String(e.subject?.id),
      name: e.subject?.name ?? `Candidate ${e.subject?.id}`,
      // BE sends the avatar under `subject.contact.image_url`.
      avatarUrl:
        e.subject?.avatar_url ?? e.subject?.contact?.image_url ?? undefined,
      // Candidate-details URL for the modal name link; undefined when unresolved.
      profileUrl: getCandidateProfileUrl(e.subject) || undefined,
    })),
    isBulk: options.isBulk ?? true,
    details: detailsFromNextAction(first.next_action),
    executionIds: immediate.map((e) => e.id),
  };
};
